import React, { useState, useEffect } from 'react';

const CurrencyInput = React.forwardRef(({
  value = 0,
  onChange,
  placeholder = '',
  className = '',
  min = 0,
  max = 999999999,
  disabled = false,
  readOnly = false,
  showZero = false,
  ...props
}, ref) => {
  const [displayValue, setDisplayValue] = useState('');

  // Update display value when value prop changes
  useEffect(() => {
    if (value === 0 && !showZero) {
      setDisplayValue('');
    } else {
      setDisplayValue(value.toString());
    }
  }, [value, showZero]);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow empty string
    if (inputValue === '') {
      setDisplayValue('');
      onChange && onChange(0);
      return;
    }

    // Allow numbers, decimal point, and limit to 2 decimal places
    const decimalValue = inputValue.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = decimalValue.split('.');
    if (parts.length > 2) {
      return; // Don't update if more than one decimal point
    }
    
    // Limit decimal places to 2
    if (parts.length === 2 && parts[1].length > 2) {
      return; // Don't update if more than 2 decimal places
    }
    
    if (decimalValue === '') {
      setDisplayValue('');
      onChange && onChange(0);
      return;
    }

    const numValue = parseFloat(decimalValue);
    
    // Check if it's a valid number
    if (isNaN(numValue)) {
      return;
    }
    
    // Check min/max constraints
    if (numValue < min) {
      setDisplayValue(min.toString());
      onChange && onChange(min);
      return;
    }
    
    if (numValue > max) {
      setDisplayValue(max.toString());
      onChange && onChange(max);
      return;
    }

    setDisplayValue(decimalValue);
    onChange && onChange(numValue);
  };

  const handleBlur = () => {
    // If empty on blur, set to 0
    if (displayValue === '') {
      setDisplayValue(showZero ? '0' : '');
      onChange && onChange(0);
    }
  };

  const handleFocus = () => {
    // If showing 0 and not showZero, clear the field on focus
    if (displayValue === '0' && !showZero) {
      setDisplayValue('');
    }
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="decimal"
      pattern="[0-9]*\.?[0-9]*"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      className={`${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${readOnly ? 'bg-gray-50' : ''}`}
      {...props}
    />
  );
});

export default CurrencyInput;
